import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, Crosshair } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { EvalDot } from "./TierBadge";

/* Pillar score bars: achieved vs max per pillar */
export const PillarBars = ({ pillars, testBase }) => (
  <div className="space-y-2" data-testid={`${testBase}-pillar-bars`}>
    {(pillars || []).map((p) => {
      const pct = p.max ? Math.round((p.score / p.max) * 100) : 0;
      return (
        <div key={p.name}>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[0.7rem] font-medium truncate">{p.name}</span>
            <span className="text-[0.68rem] tabular text-muted-foreground whitespace-nowrap">
              <b className="text-[hsl(var(--cx-ink))]">{p.score}</b> / {p.max}
            </span>
          </div>
          <div className="h-1.5 mt-1 rounded-full bg-[hsl(var(--cx-surface-2))] overflow-hidden">
            <div className={cn("h-full rounded-full",
              pct >= 90 ? "bg-[hsl(var(--status-green))]" :
              pct >= 60 ? "bg-[hsl(var(--cx-gold))]" : "bg-[hsl(var(--cx-crimson))]")}
              style={{ width: `${pct}%` }} />
          </div>
        </div>
      );
    })}
  </div>
);

/* Red-eval KPIs costing the most points */
export const FocusAreasList = ({ items, testBase }) => (
  (items || []).length === 0 ? (
    <p className="text-xs text-muted-foreground" data-testid={`${testBase}-focus-empty`}>
      No red-flag KPIs this month — nothing bleeding points.
    </p>
  ) : (
    <ul className="space-y-1.5" data-testid={`${testBase}-focus-list`}>
      {items.map((f, i) => (
        <li key={`${f.no}-${i}`}
          className="flex items-start gap-2 text-xs px-2.5 py-1.5 rounded-md border border-[hsl(var(--cx-crimson)/0.25)] bg-[hsl(var(--cx-crimson)/0.04)]"
          data-testid={`${testBase}-focus-${i}`}>
          <Crosshair size={13} className="mt-0.5 shrink-0 text-[hsl(var(--cx-crimson))]" />
          <span>
            <b>{f.name}</b>{f.section && !f.name.toLowerCase().includes(f.section.toLowerCase()) ? ` (${f.section})` : ""}: {f.score}/{f.max}
            {" — "}<b className="text-[hsl(var(--cx-crimson))]">{f.points_lost} points lost</b>
            {f.achievement ? <span className="text-muted-foreground"> · achieved {f.achievement} vs target {f.target}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  )
);

/* Full KPI table for one section, grouped by pillar, with eval dots */
export const KpiSectionTable = ({ section, pillars, month, testBase }) => {
  const [open, setOpen] = useState(false);
  const kpiCount = (pillars || []).reduce((n, p) => n + p.kpis.length, 0);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border rounded-lg">
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium hover:bg-[hsl(var(--accent))] transition-colors rounded-lg"
          data-testid={`${testBase}-kpis-${section.toLowerCase()}-toggle`}>
          {section} — full KPI table
          <span className="text-[0.65rem] text-muted-foreground font-normal">{kpiCount} KPIs</span>
          <ChevronDown size={15} className={cn("ml-auto text-muted-foreground transition-transform duration-200", open && "rotate-180")} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="overflow-x-auto">
          <Table className="cx-table min-w-[560px]" data-testid={`${testBase}-kpis-${section.toLowerCase()}-table`}>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">#</TableHead>
                <TableHead>KPI</TableHead>
                <TableHead className="text-right">Target</TableHead>
                <TableHead className="text-right">Achieved</TableHead>
                <TableHead className="text-right">Score</TableHead>
                <TableHead className="text-center pr-4">Eval</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(pillars || []).map((p) => (
                [<TableRow key={`p-${p.name}`} className="bg-[hsl(var(--cx-surface-2))]">
                  <TableCell className="pl-4 font-semibold text-[0.7rem] uppercase tracking-wide" colSpan={6}>
                    {p.name}
                  </TableCell>
                </TableRow>,
                ...p.kpis.map((k) => {
                  const mv = (k.months || {})[month] || {};
                  return (
                    <TableRow key={k.no} data-testid={`${testBase}-kpi-${k.no}`}>
                      <TableCell className="pl-4 tabular text-xs text-muted-foreground">{k.no}</TableCell>
                      <TableCell className="max-w-[260px] text-xs">{k.name}</TableCell>
                      <TableCell className="text-right tabular text-xs">{mv.target ?? "—"}</TableCell>
                      <TableCell className="text-right tabular text-xs">{mv.achievement ?? "—"}</TableCell>
                      <TableCell className="text-right tabular text-xs">
                        <b>{mv.score ?? "—"}</b><span className="text-muted-foreground"> / {k.max ?? "—"}</span>
                      </TableCell>
                      <TableCell className="text-center pr-4"><EvalDot evalColor={mv.eval} /></TableCell>
                    </TableRow>
                  );
                })]
              ))}
            </TableBody>
          </Table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
